import { supabaseManager } from "@/services/supabaseManager";
import { Organization } from "@/types";
import type { OrganizationMembership } from "@/types";
import { formatReservationTime } from "@/utils/reservationCapacity";
import {
  DEFAULT_RESERVATION_TIMEZONE,
  isValidClosedDate,
} from "@/utils/reservationClosedDays";

/** Dedupe/sort weekdays and keep only valid 0=Sun..6=Sat values. Empty -> null (unrestricted). */
function normalizeClosedWeekdays(value: unknown): number[] | null {
  if (!Array.isArray(value)) return null;
  const weekdays = Array.from(
    new Set(
      value
        .map((day) => Number(day))
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6),
    ),
  ).sort((a, b) => a - b);
  return weekdays.length > 0 ? weekdays : null;
}

/** Dedupe/sort ISO dates and drop anything that is not a real YYYY-MM-DD date. Empty -> null (unrestricted). */
function normalizeClosedDates(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const dates = Array.from(
    new Set(
      value
        .map((date) => (typeof date === "string" ? date.trim() : ""))
        .filter(isValidClosedDate),
    ),
  ).sort();
  return dates.length > 0 ? dates : null;
}

export class OrganizationService {
 private static mapDbToOrganization(dbData: any): Organization {
  return {
    id: dbData.id,
    name: dbData.name,
    slug: dbData.slug,
    slogan: dbData.slogan,
    address: dbData.address,
    phone: dbData.phone,
    email: dbData.email,
    taxRate: Number(dbData.tax_rate || 0),
    currency: dbData.currency || "USD",
    exchangeRate: Number(dbData.exchange_rate || 0),
    timezone: dbData.timezone || DEFAULT_RESERVATION_TIMEZONE,
    settings: dbData.settings || {},
    serviceChargeRate: dbData.service_charge_rate ? Number(dbData.service_charge_rate) : undefined,
    isActive: dbData.is_active,
    createdAt: new Date(dbData.created_at),
    updatedAt: new Date(dbData.updated_at),
    digitalInvoicesEnabled: dbData.digital_invoices_enabled || false,
    haciendaApiKey: dbData.mh_api_key || '',
    ivaRate: dbData.iva_rate ? Number(dbData.iva_rate) : undefined,
    reservationMaxPaxPerHour: dbData.reservation_max_pax_per_hour != null
      ? Number(dbData.reservation_max_pax_per_hour)
      : undefined,
    reservationOpensAt: formatReservationTime(dbData.reservation_opens_at),
    reservationClosesAt: formatReservationTime(dbData.reservation_closes_at),
    reservationImagePath: dbData.reservation_image_path || null,
    reservationClosedWeekdays: normalizeClosedWeekdays(dbData.reservation_closed_weekdays),
    reservationClosedDates: normalizeClosedDates(dbData.reservation_closed_dates),
  };
}

  static async getOrganizationBySlug(slug: string): Promise<Organization> {
    const client = supabaseManager.getCurrentClient();
    
    const { data: orgData, error: orgError } = await client
      .from("organizations")
      .select("*")
      .eq("slug", slug)
      .single();

    if (orgError) {  
      throw orgError;
    }

    return this.mapDbToOrganization(orgData) ;
  }

  static async getMembershipByUserAndOrg(userId: string, organizationId: string): Promise<OrganizationMembership> {
    const client = supabaseManager.getCurrentClient();
    
    const { data: membershipData, error: membershipError } = await client
      .from("organization_memberships")
      .select("*")
      .eq("user_id", userId)
      .eq("organization_id", organizationId)
      .single();

    if (membershipError) {
      throw membershipError;
    }

    if (membershipData) {
      return {
        id: membershipData.id,
        userId: membershipData.user_id,
        organizationId: membershipData.organization_id,
        role: membershipData.role,
        isActive: membershipData.is_active,
        joinedAt: new Date(membershipData.joined_at ?? membershipData.created_at ?? Date.now()),
      };
    } 
    throw new Error(`User ${userId} is not a member of organization ${organizationId}`);
  }

  static async updateOrganization(organizationId: string, updatedData: Partial<Organization>): Promise<Organization> {
    const client = supabaseManager.getCurrentClient();

    const supabaseData: Record<string, unknown> = {
      name: updatedData.name,
      slogan: updatedData.slogan,
      address: updatedData.address,
      phone: updatedData.phone,
      email: updatedData.email,
      tax_rate: updatedData.taxRate,
      currency: updatedData.currency,
      exchange_rate: updatedData.exchangeRate,
      timezone: updatedData.timezone,
      settings: updatedData.settings,
      service_charge_rate: updatedData.serviceChargeRate,
      is_active: updatedData.isActive,
      digital_invoices_enabled: updatedData.digitalInvoicesEnabled,
      mh_api_key: updatedData.haciendaApiKey,
      iva_rate: updatedData.ivaRate,
    };

    if ('reservationMaxPaxPerHour' in updatedData) {
      supabaseData.reservation_max_pax_per_hour = updatedData.reservationMaxPaxPerHour;
    }
    if ('reservationOpensAt' in updatedData) {
      supabaseData.reservation_opens_at = updatedData.reservationOpensAt;
    }
    if ('reservationClosesAt' in updatedData) {
      supabaseData.reservation_closes_at = updatedData.reservationClosesAt;
    }
    if ('reservationImagePath' in updatedData) {
      supabaseData.reservation_image_path = updatedData.reservationImagePath;
    }
    if ('reservationClosedWeekdays' in updatedData) {
      supabaseData.reservation_closed_weekdays = normalizeClosedWeekdays(
        updatedData.reservationClosedWeekdays,
      );
    }
    if ('reservationClosedDates' in updatedData) {
      supabaseData.reservation_closed_dates = normalizeClosedDates(
        updatedData.reservationClosedDates,
      );
    }

    const { data, error } = await client
      .from("organizations")
      .update(supabaseData)
      .eq("id", organizationId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return this.mapDbToOrganization(data);
  }
}
